import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const useStore = create(
  persist(
    (set, get) => ({
      // Cart state
      cart: null, // Shopify cart object
      cartLoading: false,
      
      // Quiz state  
      quizAnswers: {},
      quizResult: null,
      
      // Create a new cart with items
      createCart: async (variantIds) => {
        set({ cartLoading: true });
        try {
          const response = await fetch(`${API_URL}/api/cart/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ variantIds }),
          });
          const cart = await response.json();
          set({ cart, cartLoading: false });
          return cart;
        } catch (error) {
          console.error('Failed to create cart:', error);
          set({ cartLoading: false });
          throw error;
        }
      },
      
      // Add items to existing cart
      addToCart: async (variantIds) => {
        const { cart } = get();
        set({ cartLoading: true });
        
        try {
          // If no cart exists, create one
          if (!cart || !cart.id) {
            const newCart = await get().createCart(variantIds);
            return newCart;
          }
          
          // Add to existing cart
          const response = await fetch(`${API_URL}/api/cart/add`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              cartId: cart.id, 
              variantIds 
            }),
          });
          const updatedCart = await response.json();
          set({ cart: updatedCart, cartLoading: false });
          return updatedCart;
        } catch (error) {
          console.error('Failed to add to cart:', error);
          set({ cartLoading: false });
          throw error;
        }
      },
      
      // Remove items from cart
      removeFromCart: async (lineIds) => {
        const { cart } = get();
        if (!cart || !cart.id) return null;

        set({ cartLoading: true });
        try {
          const response = await fetch(`${API_URL}/api/cart/remove`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              cartId: cart.id,
              lineIds,
            }),
          });
          const updatedCart = await response.json();
          set({ cart: updatedCart, cartLoading: false });
          return updatedCart;
        } catch (error) {
          console.error('Failed to remove from cart:', error);
          set({ cartLoading: false });
          throw error;
        }
      },

      // Get cart details
      fetchCart: async () => {
        const { cart } = get();
        if (!cart || !cart.id) return null;
        
        try {
          const response = await fetch(`${API_URL}/api/cart/${encodeURIComponent(cart.id)}`);
          if (response.ok) {
            const cartData = await response.json();
            set({ cart: cartData });
            return cartData;
          }
          return null;
        } catch (error) {
          console.error('Failed to fetch cart:', error);
          return null;
        }
      },
      
      // Clear cart
      clearCart: () => set({ cart: null }),
      
      // Get checkout URL
      getCheckoutUrl: () => {
        const { cart } = get();
        return cart?.checkoutUrl || null;
      },
      
      // Quiz methods
      setQuizAnswers: (answers) => set({ quizAnswers: answers }),
      setQuizResult: (result) => set({ quizResult: result }),
      resetQuiz: () => set({ quizAnswers: {}, quizResult: null }),
    }),
    {
      name: 'lume-storage',
      partialize: (state) => ({
        cart: state.cart,
        quizAnswers: state.quizAnswers,
        quizResult: state.quizResult,
      }),
    }
  )
);

export default useStore;
